import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { Auth } from '@/shared/services/auth.service';
import { OpenReplay } from '@/shared/services/openreplay';
import { SSEService } from '@/shared/services/sse';
import type { Client } from '@hey-api/client-fetch';
import type { OpenApiClientOptions } from '@signal24/openapi-client-codegen';
import { client as apiAuthClient } from '../openapi-clients-generated/auth/client.gen';
import { client as apiSuiteClient } from '../openapi-clients-generated/suite/client.gen';
import { client as apiTalkClient } from '../openapi-clients-generated/talk/client.gen';
import { configureZynoOpenApiClient } from './helpers';

export function configureApiClients() {
    apiAuthClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_AUTH_API_URL
    });
    configureApiClient('auth', apiAuthClient, getAuthApiHeadersForRequest);

    apiSuiteClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_SUITE_API_URL
    });
    configureApiClient('suite', apiSuiteClient);

    apiTalkClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_TALK_API_URL
    });
    configureApiClient('talk', apiTalkClient);
}

function configureApiClient(name: string, apiClient: Client, apiHeadersFn?: OpenApiClientOptions['headers']) {
    const shouldUseDevAuthz = apiClient.getConfig().baseUrl?.startsWith('http://') && !MobileBridge.isWithinZynoSuiteMobile;
    configureZynoOpenApiClient(name, apiClient, {
        headers: apiHeadersFn ?? (shouldUseDevAuthz ? getApiHeadersForDevRequest : getApiHeadersForRequest)
    });
}

async function getAuthApiHeadersForRequest(request?: Request): Promise<Record<string, string>> {
    return getApiHeadersForRequest(request, true);
}

async function getApiHeadersForRequest(request?: Request, isAuthRequest = false): Promise<Record<string, string>> {
    return getApiHeaders(
        request ? { method: request.method, url: request.url, headers: Object.fromEntries(request.headers.entries()) } : undefined,
        isAuthRequest
    );
}

async function getApiHeadersForDevRequest(request: Request) {
    const baseHeaders = Object.fromEntries(request.headers.entries());

    const authzHeaders = await getApiHeaders({
        method: request.method,
        url: request.url,
        headers: baseHeaders
    });
    const authResponse = await fetch(`${import.meta.env.VITE_APP_AUTH_API_URL}/authz`, {
        headers: {
            ...authzHeaders,
            'x-content-type': baseHeaders['content-type'] || '',
            'x-original-url': request.url,
            'x-original-method': request.method
        }
    });
    if (!authResponse.ok) throw new Error(`Failed to fetch authz headers: ${authResponse.status} ${authResponse.statusText}`);

    const authHeaders = Object.fromEntries([...authResponse.headers.entries()].map(([key, value]) => [key.toLowerCase(), value as string]));
    for (const key in authHeaders) {
        if (key.startsWith('x-zs-')) {
            baseHeaders[key] = authHeaders[key]!;
        }
    }

    const installHeaders: Record<string, string> = import.meta.env.VITE_APP_AUTH_IID ? { 'x-zs-iid': import.meta.env.VITE_APP_AUTH_IID } : {};

    return {
        ...baseHeaders,
        ...installHeaders,
        Authorization: undefined,
        'x-zs-tz': authzHeaders['x-zs-tz']
    };
}

export async function getApiHeaders(
    request?: { method: string; url: string; headers: Record<string, string> },
    isAuthRequest = false
): Promise<Record<string, string>> {
    const baseHeaders: Record<string, string> = {
        'x-zs-tz': Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(OpenReplay.sessionId && { 'x-openreplay-sessionid': OpenReplay.sessionId }),
        ...(SSEService.connId && { 'x-sse-connection-id': SSEService.connId })
    };

    if (request && MobileBridge.isWithinZynoSuiteMobile) {
        const { headers } = await MobileBridge.sendRequest('getHeadersForRequest', { ...request, isAuthRequest });
        return { ...baseHeaders, ...headers };
    }

    const authHeaders: Record<string, string> = Auth.activeJwt ? { Authorization: `Bearer ${Auth.activeJwt}` } : {};
    const masqTid = request?.headers['x-zs-masq-tid'];

    const tenantHeaders: { 'x-zs-tid'?: string; 'x-zs-masq-tid'?: string } = masqTid
        ? {
              'x-zs-tid': Auth.impersonatorTenantId.value ?? Auth.tenantId.value!,
              'x-zs-masq-tid': masqTid
          }
        : Auth.impersonatorTenantId.value
          ? {
                'x-zs-tid': Auth.impersonatorTenantId.value,
                'x-zs-masq-tid': Auth.tenantId.value!
            }
          : Auth.tenantId.value
            ? {
                  'x-zs-tid': Auth.tenantId.value
              }
            : {};

    const accountHeaders = Auth.impersonatorUserId.value && {
        'x-zs-masq-aid': Auth.userId.value!
    };

    return {
        ...baseHeaders,
        ...authHeaders,
        ...tenantHeaders,
        ...accountHeaders
    };
}
